import type { FC, ReactElement } from 'react';
import { useDeviceType } from '../hooks/useDeviceType';
import type { DeviceType } from '../utils/getDeviceType';

// Props = {} сделано, для совместимости с {@link FC}
// biome-ignore lint/complexity/noBannedTypes: <explanation>
export type PropsWithDeviceType<Props = {}> = {
	deviceType: DeviceType;
} & Props;

type WithDeviceTypeParams<Props> =
	| [FC<Props>, FC<Props>, FC<Props>]
	| [FC<PropsWithDeviceType<Props>>];

export function withDeviceType<Props>(
	component: FC<PropsWithDeviceType<Props>>,
): FC<Props>;

export function withDeviceType<Props>(
	...args: [
		FC<PropsWithDeviceType<Props>>,
		FC<PropsWithDeviceType<Props>>,
		FC<PropsWithDeviceType<Props>>,
	]
): FC<Props>;

/**
 * Hoc для создания адаптивных компонентов.
 *
 * Параметры:
 *
 * - 1 компонент - ему будет передан пропс deviceType {@link DeviceType}.
 * Используем, если адаптация требует точечных изменений в компоненте.
 *
 * - 3 компонента - отображаются в зависимости от устройства.
 * 1 - mobile, 2 - tablet, 3 - desktop.
 *
 * Пример:
 * ```
 * export const Component = withDeviceType((props) => {
 *  const {deviceType} = props;
 *  return <div>{deviceType}</div>
 * })
 *
 * export const Component = withDeviceType(MobileComponent, TabletComponent, DesktopComponent)
 * ```
 */
export function withDeviceType<Props extends object>(
	...args: WithDeviceTypeParams<Props>
): FC<Props> {
	return (props: Props): ReactElement<Omit<Props, 'deviceType'>> => {
		const currentDeviceType = useDeviceType();

		if (args.length === 1) {
			const Component = args[0];

			return <Component {...props} deviceType={currentDeviceType} />;
		}

		const MobileComponent = args[0];
		const TabletComponent = args[1];
		const DesktopComponent = args[2];

		switch (currentDeviceType) {
			case 'tablet': {
				return <TabletComponent {...props} />;
			}
			case 'desktop': {
				return <DesktopComponent {...props} />;
			}
			default: {
				return <MobileComponent {...props} />;
			}
		}
	};
}
